<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Avatar from 'next/avatar/Avatar.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isInputFocused = ref(false);
const fenghuaiBrandColor = '#1f6feb';

const getStatusText = computed(() => {
  return props.config.isOnline
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE');
});

const quickQuestions = computed(() =>
  Array.isArray(props.config.quickQuestions) ? props.config.quickQuestions : []
);
</script>

<template>
  <div class="relative flex flex-col w-full px-4">
    <div
      v-if="config.isDefaultScreen"
      class="p-4 rounded-md shadow-sm bg-n-background dark:bg-n-solid-2"
    >
      <div class="flex items-center justify-between">
        <div>
          <div
            class="text-sm font-medium leading-4 text-n-slate-12 dark:text-n-slate-50"
          >
            {{ getStatusText }}
          </div>
          <div class="mt-1 text-xs text-n-slate-11">
            {{ config.replyTime }}
          </div>
        </div>
        <Avatar name="C" :size="34" rounded-full />
      </div>
      <button
        v-if="config.isDefaultScreen"
        class="inline-flex items-center justify-between px-2 py-1 mt-1 -ml-2 font-medium leading-6 bg-transparent rounded-md text-n-slate-12 dark:bg-transparent"
        :style="{ color: config.color }"
      >
        <span class="pr-2 text-xs">
          {{
            $t(
              'INBOX_MGMT.WIDGET_BUILDER.FOOTER.START_CONVERSATION_BUTTON_TEXT'
            )
          }}
        </span>
        <FluentIcon icon="arrow-right" size="14" />
      </button>
    </div>
    <template v-else>
      <div
        v-if="quickQuestions.length"
        class="flex gap-2 pb-2 overflow-x-auto"
      >
        <button
          v-for="question in quickQuestions"
          :key="question.id"
          type="button"
          class="flex-shrink-0 max-w-[9rem] truncate rounded-full border px-3 py-1 text-xs font-medium"
          :style="{ borderColor: fenghuaiBrandColor, color: fenghuaiBrandColor }"
        >
          {{ question.title }}
        </button>
      </div>
      <div
        class="flex items-center h-10 bg-white rounded-md dark:!bg-n-slate-3"
        :class="{ 'ring-2 ring-n-brand dark:ring-n-brand': isInputFocused }"
      >
        <ResizableTextArea
          id="chat-input"
          :rows="1"
          :placeholder="
            $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
          "
          class="flex-grow !bg-white border-0 outline-none !outline-0 border-none h-8 text-sm dark:!bg-n-slate-3 pb-0 !pt-1.5 resize-none px-3 !mb-0 focus:outline-none rounded-md"
          @focus="isInputFocused = true"
          @blur="isInputFocused = false"
        />
        <div class="flex items-center gap-2 px-2">
          <FluentIcon icon="emoji" />
          <FluentIcon class="icon-send" icon="send" />
        </div>
      </div>
    </template>
  </div>
</template>
